import clsx from 'clsx'
import { Price } from 'libs/searchspring/components/Price'

import { useBadges } from '@/components/hooks/useBadges'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { ResultQuickAdd } from 'libs/searchspring/components/ResultQuickAdd'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { globalState } from '@/components/helpers/utils'

const DEFAULT_OPTIONS = {
  hideQuickAdd: false,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', eager = false, imageSizes, imageWidths }) {
  const [variant] = result.variants
  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const criteria = {
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }

  const [badge] = useBadges(criteria)
  const [firstPromoline, secondPromoline] = usePromolines(criteria)

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `${globalState.routes.root}/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-grid.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${result.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="flex flex-col w-full grow">
        <div className="relative w-full overflow-hidden aspect-4/5">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths}>
              {!hideBadges && <Badge badge={badge} />}
            </ResultImage>
          </a>
        </div>
        <div className='flex flex-col justify-between p-4 pt-2 md:p-6 grow group-[[data-grid-variant="large"]]/product-grid:hidden'>
          <div className="flex flex-col justify-between gap-4">
            {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
            <div className={'flex flex-col pb-4'}>
              <a className="pb-1 paragraph-md md:paragraph-lg link w-fit group-hover/card:show-wavy-underline" href={url}>
                {currentResult.name}
              </a>
              <Price price={variant.price} compareAtPrice={variant.compare_at_price} className="flex gap-1" />
            </div>

          </div>
          {/* Promolines and Quick Add */}
          {(!hidePromolines || !hideQuickAdd) && (
            <div className={clsx('flex gap-2', hidePromolines ? 'justify-end' : 'justify-between')}>
            {!hidePromolines && (firstPromoline || secondPromoline) && (
              <div className="flex flex-col gap-1">
                {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="small" />}
                {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="small" />}
              </div>
            )}
            {!hideQuickAdd && currentResult.is_giftcard !== 'true' && <ResultQuickAdd result={currentResult} />}
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Badge = ({ badge }) => {
  if (!badge) return null
  const TextBadge = () => {
    const badgeClass = badge.style_variant === 'Cursive' ? 'badge max-w-[6ch] -rotate-20' : 'caption p-2 py-[6px] rounded-[99px] bg-[--bg-color]'
    return (
      <p className={clsx('absolute left-2 top-6 text-center group-[[data-grid-variant="large"]]/product-grid:hidden', badgeClass)}
        style={{ '--bg-color': badge.background_colour || 'transparent', color: badge.text_colour }}>
      {badge.title}</p>
    )
  }

  const ImageBadge = () => (
    <div className='absolute badge left-2 top-2 group-[[data-grid-variant="large"]]/product-grid:hidden'>
      <img src={badge.image} alt={badge.title || 'Badge'} />
    </div>
  )
  return (
    badge.image ? <ImageBadge /> : <TextBadge />
  )
}
